import { isPossiblePhoneNumber } from "react-phone-number-input";
import { IScheduleRequest } from "../types/api/agent-settings";
import { ITimeSlotRequest } from "../types/api/agent-settings";
import {
  ISchedule,
  ITimeSlot,
  IOrderingConfig,
  PaymentProvider,
  PaymentStrategy,
  TimeString,
  IPrepTimeSchedule,
  IPrepTimeSlot,
  ISettingsValidationErrors,
} from "../types/agent-settings";
import { IFAQ } from "../types/faq";
import {
  MAX_ANSWER_LENGTH,
  MAX_QUESTION_LENGTH,
} from "../components/feature/settings/faq/faq-constants";
import { MAX_GREETING_LENGTH } from "../components/feature/settings/greeting/greeting-constants";
import { CountryCode } from "libphonenumber-js";

// payments feature is enabled if pickup payment module config has the stripe provider and the strategy is required
export function isPickupPaymentsEnabled(
  orderingConfig: IOrderingConfig | null
): boolean {
  const paymentConfig = orderingConfig?.pickupModuleConfig?.paymentModuleConfig;
  if (!paymentConfig) {
    return false;
  }

  return (
    paymentConfig.provider === PaymentProvider.STRIPE &&
    "strategy" in paymentConfig &&
    (paymentConfig.strategy === PaymentStrategy.REQUIRED ||
      paymentConfig.strategy === PaymentStrategy.REQUIRED_ABOVE)
  );
}

export function isDeliveryPaymentsEnabled(
  orderingConfig: IOrderingConfig | null
): boolean {
  const paymentConfig =
    orderingConfig?.deliveryModuleConfig?.paymentModuleConfig;
  if (!paymentConfig) {
    return false;
  }

  return (
    paymentConfig.provider === PaymentProvider.STRIPE &&
    "strategy" in paymentConfig &&
    (paymentConfig.strategy === PaymentStrategy.REQUIRED ||
      paymentConfig.strategy === PaymentStrategy.REQUIRED_ABOVE)
  );
}

export function isPickupTipPromptEnabled(
  orderingConfig: IOrderingConfig | null
): boolean {
  const paymentConfig = orderingConfig?.pickupModuleConfig?.paymentModuleConfig;
  if (!paymentConfig) {
    return false;
  }

  return (
    paymentConfig.provider === PaymentProvider.STRIPE &&
    "requireTip" in paymentConfig &&
    paymentConfig.requireTip === true
  );
}

export function getPickupPaymentThreshold(
  orderingConfig: IOrderingConfig | null
): number {
  const paymentConfig = orderingConfig?.pickupModuleConfig?.paymentModuleConfig;
  if (!paymentConfig || paymentConfig.provider !== PaymentProvider.STRIPE) {
    return 0;
  }

  return "requiredAboveThreshold" in paymentConfig
    ? paymentConfig.requiredAboveThreshold || 0
    : 0;
}

export function getDeliveryPaymentThreshold(
  orderingConfig: IOrderingConfig | null
): number {
  const paymentConfig =
    orderingConfig?.deliveryModuleConfig?.paymentModuleConfig;
  if (!paymentConfig || paymentConfig.provider !== PaymentProvider.STRIPE) {
    return 0;
  }

  return "requiredAboveThreshold" in paymentConfig
    ? paymentConfig.requiredAboveThreshold || 0
    : 0;
}

export function isPickupPaymentThresholdEnabled(
  orderingConfig: IOrderingConfig | null
): boolean {
  const paymentConfig = orderingConfig?.pickupModuleConfig?.paymentModuleConfig;
  if (!paymentConfig || paymentConfig.provider !== PaymentProvider.STRIPE) {
    return false;
  }

  return (
    "strategy" in paymentConfig &&
    paymentConfig.strategy === PaymentStrategy.REQUIRED_ABOVE
  );
}

export function isDeliveryPaymentThresholdEnabled(
  orderingConfig: IOrderingConfig | null
): boolean {
  const paymentConfig =
    orderingConfig?.deliveryModuleConfig?.paymentModuleConfig;
  if (!paymentConfig || paymentConfig.provider !== PaymentProvider.STRIPE) {
    return false;
  }

  return (
    "strategy" in paymentConfig &&
    paymentConfig.strategy === PaymentStrategy.REQUIRED_ABOVE
  );
}

// Retell
const backendVoiceSpeedRange = [0.8, 1.3];
const frontendVoiceSpeedRange = [0, 100];
const backendVolumeRange = [0.5, 1.5];
const frontendVolumeRange = [1, 100];

export function transformVoiceSpeedToFrontend(speed: number): number {
  if (speed === 0.975) {
    return 40;
  }
  if (speed === 1.0) {
    return 50;
  }
  return transformRange(
    speed,
    backendVoiceSpeedRange[0],
    backendVoiceSpeedRange[1],
    frontendVoiceSpeedRange[0],
    frontendVoiceSpeedRange[1]
  );
}

export function transformVoiceSpeedToBackend(speed: number): number {
  // normal speed is set 1.0
  if (speed == 40) {
    return 0.975;
  }
  if (speed === 50) {
    return 1.0;
  }

  return transformRange(
    speed,
    frontendVoiceSpeedRange[0],
    frontendVoiceSpeedRange[1],
    backendVoiceSpeedRange[0],
    backendVoiceSpeedRange[1],
    false
  );
}

export function transformVolumeToFrontend(volume: number): number {
  if (volume === 1.0) {
    return 50;
  }
  return Math.round(
    transformRange(
      volume,
      backendVolumeRange[0],
      backendVolumeRange[1],
      frontendVolumeRange[0],
      frontendVolumeRange[1]
    )
  );
}

export function transformVolumeToBackend(volume: number): number {
  // normal volume is set 1.0
  if (volume === 50) {
    return 1.0;
  }
  return transformRange(
    volume,
    frontendVolumeRange[0],
    frontendVolumeRange[1],
    backendVolumeRange[0],
    backendVolumeRange[1],
    true
  );
}

export function validatePhoneNumber(
  phoneNumber: string | null,
  countryCode?: CountryCode | undefined
): boolean {
  if (countryCode) {
    return (
      !!phoneNumber && isPossiblePhoneNumber(phoneNumber.trim(), countryCode)
    );
  }
  return !!phoneNumber && isPossiblePhoneNumber(phoneNumber.trim());
}

export function validateGreeting(greeting: string | null): boolean {
  const trimmedGreeting = greeting?.trim();
  return (
    !!trimmedGreeting &&
    trimmedGreeting.length > 0 &&
    trimmedGreeting.length <= MAX_GREETING_LENGTH
  );
}

export function validateFaqs(faqs: IFAQ[]): boolean {
  return faqs.every((faq) => validateFaq(faq));
}

export function validateFaq(faq: IFAQ): boolean {
  const trimmedQuestion = faq.question.trim();
  const trimmedAnswer = faq.answer.trim();
  return (
    !!trimmedQuestion &&
    trimmedQuestion.length > 0 &&
    !!trimmedAnswer &&
    trimmedAnswer.length > 0 &&
    trimmedQuestion.length <= MAX_QUESTION_LENGTH &&
    trimmedAnswer.length <= MAX_ANSWER_LENGTH
  );
}

function toMinutes(time: TimeString): number {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
}
export function isTimeWithinBounds(
  time: TimeString,
  start: TimeString,
  end: TimeString
): boolean {
  const t = toMinutes(time);
  const s = toMinutes(start);
  const e = toMinutes(end);

  if (s <= e) {
    // Normal case (e.g., 09:00–17:00)
    return t >= s && t <= e;
  } else {
    // Overnight case (e.g., 22:00–06:00)
    return t >= s || t <= e;
  }
}
/**
 * Check whether a single time string falls within any of the provided bounds (array of ITimeSlot).
 * Returns true if bounds is empty/undefined (no restriction) or time is null/empty.
 */
export function isTimeInAllowedBounds(
  time: TimeString | null,
  bounds?: ITimeSlot[] | null
): boolean {
  if (!time) return true;
  if (!bounds || bounds.length === 0) return true;
  return bounds.some((b) => {
    if (!b.open || !b.close) return false;
    return isTimeWithinBounds(time, b.open, b.close);
  });
}

/**
 * Validate that every orderingHours slot is contained within at least one schedule slot for the same day.
 * - If orderingHours is null -> valid
 * - If schedule is null -> treat as no bounds and return true
 */
export function validateOrderingHours(
  orderingHours: ISchedule | null,
  schedule: ISchedule | null
): boolean {
  if (!orderingHours) return true;
  if (!schedule) return true;

  return Object.entries(orderingHours).every(
    ([day, orderingSlots]: [string, ITimeSlot[]]) => {
      const scheduleSlots = schedule[day as keyof ISchedule] || [];
      // if there are no ordering slots for the day, it's fine
      if (!orderingSlots || orderingSlots.length === 0) return true;

      return orderingSlots.every((oSlot: ITimeSlot) => {
        // empty/partial ordering slots are considered valid here (other validators handle completeness)
        if (!oSlot.open || !oSlot.close) return true;

        // ordering slot must be fully contained within at least one schedule slot
        return scheduleSlots.some((sSlot: ITimeSlot) => {
          if (!sSlot.open || !sSlot.close) return false;
          // both open and close must be within the schedule slot bounds
          return (
            isTimeWithinBounds(
              oSlot.open as TimeString,
              sSlot.open as TimeString,
              sSlot.close as TimeString
            ) &&
            isTimeWithinBounds(
              oSlot.close as TimeString,
              sSlot.open as TimeString,
              sSlot.close as TimeString
            )
          );
        });
      });
    }
  );
}

export function validateSchedule(schedule: ISchedule | null): boolean {
  if (!schedule) return true;
  return Object.values(schedule).every((slots) =>
    slots.every((slot: ITimeSlot) => validateTimeSlot(slot))
  );
}
function validateTimeSlot(slot: ITimeSlot): boolean {
  return (
    (!!slot.open && !!slot.close) || (slot.open == null && slot.close == null)
  );
}

export function validatePrepTimeMinutes(prepTime: string | undefined): boolean {
  if (prepTime === undefined || prepTime === "") return true;
  const numericValue = parseInt(prepTime);
  return !isNaN(numericValue) && numericValue >= 10 && numericValue <= 24 * 60;
}

function validationErrorPrepTimeMinutes(
  prepTime: string | null,
  type: "pickup" | "delivery" | "" = ""
): string | undefined {
  if (!prepTime) return `Please enter a ${type} prep time.`;
  const numericValue = parseInt(prepTime);
  if (isNaN(numericValue)) return "Please enter a valid number.";
  if (numericValue < 10)
    return `Please enter a ${type} prep time of at least 10 minutes.`;
  if (numericValue > 24 * 60)
    return `Please enter a ${type} prep time less than 1440 minutes.`;
  return;
}

export function validationErrorsDefaultPrepTime({
  pickupPrepTime,
  deliveryPrepTime,
  pickupEnabled,
  deliveryEnabled,
}: {
  pickupPrepTime: string | null;
  deliveryPrepTime: string | null;
  pickupEnabled: boolean;
  deliveryEnabled: boolean;
}) {
  const newErrors: Pick<
    ISettingsValidationErrors,
    "pickupPrepTime" | "deliveryPrepTime"
  > = {};
  if (pickupEnabled) {
    newErrors.pickupPrepTime = validationErrorPrepTimeMinutes(
      pickupPrepTime,
      "pickup"
    );
  }
  if (deliveryEnabled) {
    newErrors.deliveryPrepTime = validationErrorPrepTimeMinutes(
      deliveryPrepTime,
      "delivery"
    );
    if (
      !newErrors.deliveryPrepTime &&
      !newErrors.pickupPrepTime &&
      parseInt(deliveryPrepTime!) < parseInt(pickupPrepTime!)
    ) {
      newErrors.deliveryPrepTime =
        "Delivery prep time cannot be less than pickup prep time.";
    }
  }
  return newErrors;
}

export function validatePrepTimeScheduleFields(
  schedule: IPrepTimeSchedule | null | undefined
): boolean {
  if (!schedule) return true;
  return Object.values(schedule).every(validatePrepTimeSlotFields);
}

function validatePrepTimeSlot(slot: IPrepTimeSlot): boolean {
  const baseValid =
    (!!slot.open && !!slot.close) || (slot.open == null && slot.close == null);
  if (!baseValid) return false;
  const deliveryValid =
    slot.delivery == null || validatePrepTimeMinutes(slot.delivery);
  const pickupValid =
    slot.pickup == null || validatePrepTimeMinutes(slot.pickup);
  return deliveryValid && pickupValid;
}

function validatePrepTimeSlotFields(slots: IPrepTimeSlot[]): boolean {
  if (!slots || slots.length === 0) return true;
  return slots.every(validatePrepTimeSlot);
}

export function validateScheduleHasNoOverlappingSlots(
  schedule: IPrepTimeSchedule | null | undefined
): boolean {
  if (!schedule) return true;
  return Object.values(schedule).every((slots) => !hasOverlappingSlots(slots));
}

// Return true if any fully specified slots within a day overlap (after handling overnight ranges)
export function hasOverlappingSlots(
  slots: IPrepTimeSlot[] | ITimeSlot[]
): boolean {
  const sorted = slots
    .filter((s) => !!s.open && !!s.close)
    .map((s) => ({
      openMinutes: timeToMinutes(s.open as string),
      closeMinutes: timeToMinutes(s.close as string),
    }))
    .map((s) => {
      let close = s.closeMinutes;
      if (close < s.openMinutes) {
        close = close + 24 * 60; // overnight span
      }
      return { ...s, closeMinutes: close };
    })
    .sort((a, b) => a.openMinutes - b.openMinutes);

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    if (curr.openMinutes < prev.closeMinutes) {
      return true;
    }
  }
  return false;
}

function transformRange(
  value: number,
  fromMin: number,
  fromMax: number,
  toMin: number,
  toMax: number,
  shouldRound = true
): number {
  const normalizedValue =
    ((value - fromMin) / (fromMax - fromMin)) * (toMax - toMin) + toMin;
  return shouldRound ? Number(normalizedValue.toFixed(3)) : normalizedValue;
}

export function mergeOverlappingSlots(
  schedule: IScheduleRequest
): IScheduleRequest {
  Object.entries(schedule).forEach(([day, slots]) => {
    schedule[day as keyof IScheduleRequest] = mergeSlots(slots);
  });
  return schedule;
}

function mergeSlots(slots: ITimeSlotRequest[]): ITimeSlotRequest[] {
  if (slots.length <= 1) return slots;

  // Sort slots by opening time
  const sortedSlots = [...slots].sort((a, b) => {
    if (!a.open || !b.open) return 0;
    return a.open.localeCompare(b.open);
  });

  const merged: ITimeSlotRequest[] = [];
  let current = sortedSlots[0];

  for (let i = 1; i < sortedSlots.length; i++) {
    const next = sortedSlots[i];

    if (!current.open || !current.close || !next.open || !next.close) continue;

    const currentClose = timeToMinutes(current.close);
    const nextOpen = timeToMinutes(next.open);
    const nextClose = timeToMinutes(next.close);

    // If slots overlap or are adjacent
    if (currentClose >= nextOpen) {
      // Merge by taking the later closing time
      current = {
        ...current,
        close: minutesToTime(Math.max(currentClose, nextClose)),
      };
    } else {
      merged.push(current);
      current = next;
    }
  }

  merged.push(current);
  return merged;
}

function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
}

function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins
    .toString()
    .padStart(2, "0")}`;
}

export function isSlotSplitNeeded(slot: ITimeSlot): boolean {
  if (!slot.open || !slot.close) return false;
  const [hour1, min1] = slot.open.split(":").map(Number);
  const [hour2, min2] = slot.close.split(":").map(Number);
  return hour2 < hour1 || (hour2 === hour1 && min2 < min1);
}
